import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { maiorQueZeroValidator } from '../../../../../../shared/validators/maior-que-zero.validator';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';
import { AbntPipe } from '../../../../../../shared/pipes/abnt.pipe';
import { RegraDoMultiplo, quantidadeDoMultiplo, unidadesDoMultiplo } from '../venda-item.multiplo';

export interface VdMultiploDialogData {
    descricao: string;
    codigoSku: string | null;
    /** Sigla da unidade do produto (a da quantidade calculada). */
    unidade: string | null;
    regra: RegraDoMultiplo;
    /** Quantidade que já está na linha (reabertura pela grade); nula no lançamento. */
    quantidadeAtual: number | null;
    /** A unidade do produto aceita fração; sem isso, a quantidade calculada tem de ser inteira. */
    isUnidadeMedidaFracionada: boolean;
    casasDecimaisQuantidade: number;
    /** Máscara da quantidade de múltiplos: a decimal da empresa ou a inteira, pela unidade do produto. */
    mascaraQuantidade: Partial<NgxCurrencyConfig>;
}

/**
 * "Lançamento de múltiplos" (§6.2.3): o vendedor informa quantas EMBALAGENS, e a quantidade do
 * item é o produto pela regra do cadastro — refeito a cada digitação, em leitura. Abre ao escolher
 * o produto e ao tentar editar a Qtde na grade; devolve a quantidade calculada, ou nada.
 */
@Component({
    selector: 'app-vd-multiplo-dialog',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        NgxCurrencyDirective,
        AbntPipe,
    ],
    templateUrl: './vd-multiplo-dialog.component.html',
    styleUrl: './vd-multiplo-dialog.component.css',
})
export class VdMultiploDialogComponent {
    public readonly form: FormGroup;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<VdMultiploDialogComponent, number | undefined>,
        @Inject(MAT_DIALOG_DATA) public data: VdMultiploDialogData,
    ) {
        const atual = Number(data.quantidadeAtual ?? 0);
        const unidades = atual > 0 ? unidadesDoMultiplo(atual, data.regra.fatorMultiplo) : 1;

        // Unidade que não fraciona: `múltiplos × fator` tem de dar inteiro (o fator pode ter casas).
        const quantidadeInteira: ValidatorFn = (controle) =>
            !data.isUnidadeMedidaFracionada
                && quantidadeDoMultiplo(Number(controle.value ?? 0), data.regra.fatorMultiplo, data.casasDecimaisQuantidade) % 1 !== 0
                ? { quantidadeFracionada: true }
                : null;

        this.form = this.fb.group({
            unidades: [unidades, [Validators.required, maiorQueZeroValidator(), quantidadeInteira]],
        });
    }

    public get rotuloUnidades(): string {
        return this.data.regra.unidadeMultiplo ? `Qtde. ${this.data.regra.unidadeMultiplo}` : 'Qtde. de múltiplos';
    }

    /** `Qtde. {unidade} × Múltiplo`, nas casas de quantidade — o que vai para a linha. */
    public get quantidade(): number {
        return quantidadeDoMultiplo(
            Number(this.form.get('unidades')?.value ?? 0),
            this.data.regra.fatorMultiplo,
            this.data.casasDecimaisQuantidade,
        );
    }

    public confirmar(): void {
        if (this.form.invalid || this.quantidade <= 0) {
            this.form.markAllAsTouched();
            return;
        }

        this.dialogRef.close(this.quantidade);
    }

    public cancelar(): void {
        this.dialogRef.close(undefined);
    }
}
